/**
 * "Live now" banner: polls the server's room registry and surfaces any
 * occupied live-lesson room the user belongs to — deliberately independent of
 * the lesson schedule, so a session that started late or is running over time
 * stays visible and joinable as long as someone is in the room.
 */

import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { api } from '../lib/api.ts';
import { t } from '../lib/i18n.ts';
import { Button, Card } from '../lib/ui.tsx';

export interface ActiveRoom {
  lessonId: string;
  coach: boolean;
  student: boolean;
  startsAt: string | null;
  endsAt: string | null;
  otherName: string | null;
}

export function useActiveRooms() {
  return useQuery({
    queryKey: ['live-active'],
    queryFn: () => api<ActiveRoom[]>('/api/live/active'),
    refetchInterval: 15_000,
  });
}

export default function LiveNowBanner({ role }: { role: 'coach' | 'student' }) {
  const { data } = useActiveRooms();
  const rooms = (data ?? []).filter((r) => (role === 'coach' ? r.student : r.coach));
  if (rooms.length === 0) return null;

  return (
    <>
      {rooms.map((r) => {
        const overtime = r.endsAt && new Date(r.endsAt) < new Date();
        return (
          <Card key={r.lessonId} className="animate-fade-up border-brand-300 bg-gradient-to-r from-brand-50 to-brand-100/70">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="flex items-center gap-2 text-sm font-semibold text-brand-900">
                  <span aria-hidden className="relative flex size-2.5">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500 opacity-60" />
                    <span className="relative inline-flex size-2.5 rounded-full bg-red-500" />
                  </span>
                  {r.otherName ?? (role === 'coach' ? t('live.yourStudent', 'Your student') : t('live.yourCoach', 'Your coach'))}{' '}
                  {t('live.inRoom', 'is in the live room')}
                </p>
                <p className="mt-0.5 text-xs text-brand-800">
                  {overtime
                    ? t('live.overtime', 'The scheduled time is over, but the session is still live — you can join anytime.')
                    : t('live.joinNow', 'The session is live right now.')}
                </p>
              </div>
              <Link to={role === 'coach' ? `/coach/live/${r.lessonId}` : `/app/live/${r.lessonId}`}>
                <Button>{t('live.joinRoom', 'Join live room')}</Button>
              </Link>
            </div>
          </Card>
        );
      })}
    </>
  );
}
